/// <amd-module name="Wiki/Editor/EditorDialogsHandler" />
import * as React from "react";

import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssLayoutManager, WrappedComponent } from "VSS/Platform/Layout";
import { combineUrlPaths } from "VSS/Platform/Util/Url";

import { WikiPageResponse } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { CloseEditorDialog } from "Wiki/Editor/Components/CloseEditorDialog";
import { SavePageDialog } from "Wiki/Editor/Components/SavePageDialog";
import { IWikiEditorState } from "Wiki/Editor/Contracts";
import { PageSaveSource } from "Wiki/Editor/PageSaveSource";
import * as Resources from "Wiki/Editor/Resources";
import { WikiInfo, WikiPageInfoWithName } from "Wiki/ViewCommon/Contracts";
import { getFileName } from "Wiki/ViewCommon/Utils/PathHelper";

export function showCloseDialog(pageContext: IVssPageContext, onClose: () => void): void {
    const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
    layoutManager.renderCallout(onDismiss => {
        return <CloseEditorDialog onDismiss={onDismiss} closeEditor={onClose} />;
    });
}

export function showSaveDialog(
    pageContext: IVssPageContext,
    showRenameInfo: boolean,
    withCustomMessage: boolean,
    savePage: (commitMessage?: string) => void,
    onDismissDialog: () => void,
    defaultMessage?: string
): void {
    const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
    layoutManager.renderCallout(onDismiss => {
        return (
            <SavePageDialog
                onDismiss={() => {
                    onDismiss();
                    onDismissDialog();
                }}
                savePage={savePage}
                showRenameInfo={showRenameInfo}
                withCustomMessage={withCustomMessage}
                defaultMessage={defaultMessage}
            />
        );
    });
}

export function showRenamePageDialog(
    pageContext: IVssPageContext,
    onSave: () => void,
    onDismissRenameDialog: (response?: WikiPageResponse, savedAttachments?: string[]) => void,
    wikiInfo: WikiInfo,
    pageInfo: WikiPageInfoWithName,
    editorState: IWikiEditorState,
    commitMessage: string
): void {
    const pageSaveSource = new PageSaveSource(pageContext);
    const oldPagePath = combineUrlPaths(pageInfo.parentPath, pageInfo.pageName);
    const newPagePath = combineUrlPaths(pageInfo.parentPath, editorState.pageName);

    const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
    layoutManager.renderCallout(onDismiss => {
        return (
            <WrappedComponent
                wrappedType={"Wiki.PageRenamePanel"}
                dependencies={["ms.vss-wiki-web.page-rename-panel-content", "ms.vss-wiki-web.search-location-include"]}
                onConfirm={(closeWithoutFixing: boolean): Promise<WikiPageResponse | void> => {
                    if (closeWithoutFixing) {
                        onSave();
                        return Promise.resolve(undefined);
                    } else {
                        return pageSaveSource.savePage(wikiInfo, pageInfo, editorState, commitMessage);
                    }
                }}
                newPagePath={newPagePath}
                oldPagePath={oldPagePath}
                wiki={wikiInfo.wiki}
                wikiVersion={wikiInfo.wikiVersion}
                title={format(Resources.PageRenamePanel_TitleMessage, getFileName(oldPagePath))}
                description={format(Resources.PageRenamePanel_BrokenLinkFixing_Description, getFileName(oldPagePath), getFileName(newPagePath))}
                successMessage={Resources.PageRenamePanel_SuccessMessage}
                brokenLinksDiscoveryOrRenameFailureMessage={Resources.PageRenamePanel_BrokenLinksDiscoveryOrRenameFailureMessage}
                linksFixFailureMessage={Resources.PageRenamePanel_LinksFixFailureMessage}
                updateButtonText={Resources.RenamePagePanel_RenameButtonText}
                onDismiss={(response: WikiPageResponse) => {
                    onDismiss();
                    if (response) {
                        onDismissRenameDialog(response, pageSaveSource.savedAttachments);
                    } else {
                        onDismissRenameDialog(undefined, pageSaveSource.savedAttachments);
                    }
                }}
            />
        );
    });
}
